import React, { useCallback, useMemo, useRef, useState } from 'react'
import { ActivityIndicator, ScrollView, TouchableOpacity, View, Platform, useWindowDimensions } from 'react-native'
import { Hashtags, Header, Icon, Screen, Text, FeatureTooltip } from '@components'
import { useNavigation, useTheme } from '@react-navigation/native'
import { getCommonContactsStyles, getMergeContactsStyles } from './styles'
import { ClassifyContactsProps } from './types';
import { getCommonStyles, Scope } from '@utils'
import { ProfileImage } from '../../../screens/profile/components'
import ButtonSwipe from './components/ButtonSwipe'
import { useScreen } from '@hooks';
import { useLanguage } from '@context'
import { useFtClassifyContacts, useUpdateContactScope, useUpdateGhostProfileScope } from '@hooks';
import type { Contact } from '@hooks';
import { useCurrentUserStore } from '@store'
import {
  addClassifiedId,
  clearClassifiedIds,
  loadClassifiedIds,
  pruneClassifiedIds,
  removeClassifiedId,
} from '../../../utils/classifiedProfiles'
import TinderCard from 'react-tinder-card'

// Conditional imports for mobile-only features
let Carousel: any = null
let ICarouselInstance: any = null
let Gesture: any = null
let GestureDetector: any = null
let runOnJS: any = null

if (Platform.OS !== 'web') {
  try {
    const carouselModule = require('react-native-reanimated-carousel')
    const gestureModule = require('react-native-gesture-handler')
    const reanimatedModule = require('react-native-reanimated')

    Carousel = carouselModule.default
    ICarouselInstance = carouselModule.ICarouselInstance
    Gesture = gestureModule.Gesture
    GestureDetector = gestureModule.GestureDetector
    runOnJS = reanimatedModule.runOnJS
  } catch (error) {
    console.warn('Mobile carousel dependencies not available:', error)
  }
}

const ProfileCard = ({contact, onPress}: {contact: Contact, onPress?: () => void}) => {
  const theme = useTheme()
  const commonStyles = getCommonStyles(theme)
  const styles = getCommonContactsStyles(theme)
  const containerRef = useRef<View>(null)
  const mouseDownPos = useRef({ x: 0, y: 0 })

  // On web, TinderCard captures mouse events and prevents normal click handling.
  // We attach a native DOM click listener directly to bypass this.
  // mousedown is NOT stopped so TinderCard can detect drag; we distinguish
  // a click from a drag by checking how far the mouse moved.
  React.useEffect(() => {
    if (Platform.OS !== 'web' || !onPress || !containerRef.current) return
    const node = containerRef.current as unknown as HTMLElement
    if (!node?.addEventListener) return

    const handleMouseDown = (e: MouseEvent) => {
      mouseDownPos.current = { x: e.clientX, y: e.clientY }
    }
    const handleClick = (e: MouseEvent) => {
      const dx = Math.abs(e.clientX - mouseDownPos.current.x)
      const dy = Math.abs(e.clientY - mouseDownPos.current.y)
      if (dx > 5 || dy > 5) return // was a drag, ignore
      e.stopPropagation()
      onPress()
    }

    node.addEventListener('mousedown', handleMouseDown)
    node.addEventListener('click', handleClick)
    return () => {
      node.removeEventListener('mousedown', handleMouseDown)
      node.removeEventListener('click', handleClick)
    }
  }, [onPress])

  return (
    <View style={styles.card}>
      <View
        ref={containerRef}
        style={{
          alignItems: 'center',
          gap: theme.spacing.md,
          cursor: onPress ? 'pointer' : undefined,
        } as any}
      >
        <View style={[commonStyles.center, {alignSelf: 'center'}]}>
          <ProfileImage isBig isGhost={contact.isGhost} avatarUrl={contact?.avatarUrl} />
        </View>
        <Text type='titleBold' center>{contact.fullName}</Text>
      </View>
      {contact.professions && contact.professions.length > 0 && (
        <View style={{flexDirection: 'row', flexWrap: 'wrap', gap: theme.spacing.xs, justifyContent: 'center'}}>
          {contact.professions.slice(0, 2).map((p) => (
            <View key={p.id} style={{
              maxWidth: '100%',
              backgroundColor: theme.colors.primary + '15',
              borderRadius: theme.border.jumbo,
              paddingHorizontal: theme.spacing.md,
              paddingVertical: theme.spacing.xxs + 1,
            }}>
              <Text type='bodyExtraSmall' color={theme.colors.primary} numberOfLines={1}>{p.name}</Text>
            </View>
          ))}
        </View>
      )}
      {contact.hashtags && contact.hashtags.length > 0 && (
        <Hashtags hashtags={contact.hashtags} hasBackground/>
      )}
    </View>
  );
}

// Smallest card that still shows photo, name, jobs and hashtags (web).
const CARD_MIN_HEIGHT = 340

export const ClassifyContacts: React.FC<ClassifyContactsProps> = () => {
  const theme = useTheme()
  const { t } = useLanguage()
  const { width: windowWidth, height: windowHeight } = useWindowDimensions()
  const carouselHeight = Math.round(
    Math.min(Math.max(windowHeight * 0.48, 380), windowHeight - 220),
  )
  const { isWeb, isDesktop, width } = useScreen()
  const styles = getMergeContactsStyles(theme)
  const commonStyles = getCommonStyles(theme)
  const commonContactsStyles = getCommonContactsStyles(theme)
  const navigation = useNavigation()
  // On a short browser window the card keeps its size and the page scrolls,
  // instead of squeezing the card until its text spills out.
  const Body: React.ElementType = isWeb ? ScrollView : React.Fragment
  const bodyProps = isWeb ? { style: { flex: 1 }, contentContainerStyle: { flexGrow: 1 } } : {}

  const { userID } = useCurrentUserStore()
  const { data: contacts = [], isLoading, isError, isSuccess } = useFtClassifyContacts()
  const scopeMutation = useUpdateContactScope()
  const ghostScopeMutation = useUpdateGhostProfileScope()

  // A ghost profile changes scope through its own endpoint; a real contact
  // through the contact-scope endpoint. The classify list now mixes both.
  const applyScope = useCallback(
    (contact: Contact, scope: Scope) => {
      // Sorted: not proposed again at the next visit, unless the save is refused.
      if (userID) addClassifiedId(userID, contact.id)
      // The promise of each save, not a callback given to mutate(): that one is
      // dropped as soon as the next card is sorted or the screen is left.
      const saving = contact.isGhost
        ? ghostScopeMutation.mutateAsync({ ghostProfileId: contact.id, scope })
        : scopeMutation.mutateAsync({ contactId: contact.id, scope })
      saving.catch(() => {
        if (userID) removeClassifiedId(userID, contact.id)
      })
    },
    [ghostScopeMutation, scopeMutation, userID],
  )

  // What was already sorted on this device when the screen opened. It is not
  // updated during the visit: the pile's indexes depend on the list length, and
  // removedContactIds keeps doing that job.
  const [classifiedAtOpen, setClassifiedAtOpen] = useState<Set<string> | null>(null)
  React.useEffect(() => {
    let isCurrent = true
    const loading = userID ? loadClassifiedIds(userID) : Promise.resolve([])
    loading.then((ids) => {
      if (isCurrent) setClassifiedAtOpen(new Set(ids))
    })
    return () => {
      isCurrent = false
    }
  }, [userID])

  // Deleted cards do not pile up in the memory of the device.
  React.useEffect(() => {
    if (userID && isSuccess) pruneClassifiedIds(userID, contacts.map((contact) => contact.id))
  }, [userID, isSuccess, contacts])

  const [removedContactIds, setRemovedContactIds] = useState<string[]>([])
  const filteredContacts = useMemo(() =>
    contacts.filter(
      contact => !removedContactIds.includes(contact.id) && !classifiedAtOpen?.has(contact.id),
    ),
    [contacts, removedContactIds, classifiedAtOpen]
  )

  const [currentIndex, setCurrentIndex] = useState(filteredContacts.length - 1)

  React.useEffect(() => {
    setCurrentIndex(filteredContacts.length - 1)
  }, [filteredContacts.length])

  const ref = React.useRef<any>(null);
  const nativeCarouselIndexRef = useRef(0);
  const currentIndexRef = useRef(currentIndex)

  const childRefs = useMemo(
    () =>
      Array(filteredContacts.length)
        .fill(null)
        .map(() => React.createRef()),
    [filteredContacts.length]
  )

  const canGoBack = currentIndex < filteredContacts.length - 1
  const canSwipe = currentIndex >= 0

  const rollback = async () => {
    if(!canGoBack) return
    const newIndex = currentIndex + 1
    updateCurrentIndex(newIndex)
    const restoredId = removedContactIds[removedContactIds.length - 1]
    if (userID && restoredId) removeClassifiedId(userID, restoredId)
    setRemovedContactIds(prev => prev.slice(0, -1))
    // @ts-ignore
    await childRefs[newIndex].current?.restoreCard()
  }

  const swipe = async (dir: 'left' | 'right') => {
    if(!canSwipe || currentIndex >= filteredContacts.length) return
    // @ts-ignore
    await childRefs[currentIndex].current?.swipe(dir)
  }

  const updateCurrentIndex = (val: number) => {
    setCurrentIndex(val)
    currentIndexRef.current = val
  }

  const handleSwipe = (direction: string, contact: Contact, idx: number) => {
    setRemovedContactIds(prev => [...prev, contact.id])
    updateCurrentIndex(idx - 1)

    const scope = direction === 'right' ? Scope.PROFESSIONAL : Scope.PRIVATE
    applyScope(contact, scope)
  }

  const outOfFrame = (name: string, idx: number) => {
    // @ts-ignore
    currentIndexRef.current >= idx && childRefs[idx].current?.restoreCard()
  }

  /** Native: swipe left = Private, swipe right = Pro (same as web TinderCard). Uses scrollTo like MergeContacts. */
  const handleNativeClassifySwipe = useCallback(
    (direction: 'left' | 'right') => {
      if (Platform.OS === 'web' || !ref.current) return;
      const list = filteredContacts;
      if (!list.length) return;
      const i =
        typeof ref.current.getCurrentIndex === 'function'
          ? ref.current.getCurrentIndex()
          : nativeCarouselIndexRef.current;
      nativeCarouselIndexRef.current = i;
      const contact = list[i];
      if (!contact?.id) return;

      const contactId = contact.id;
      const oldLen = list.length;
      const scope = direction === 'right' ? Scope.PROFESSIONAL : Scope.PRIVATE;

      const apply = () => {
        setRemovedContactIds((prev) =>
          prev.includes(contactId) ? prev : [...prev, contactId],
        );
        applyScope(contact, scope);
        const newLen = oldLen - 1;
        if (newLen > 0) {
          const targetIndex = Math.min(i, newLen - 1);
          requestAnimationFrame(() => {
            ref.current?.scrollTo?.({ index: targetIndex, animated: false });
            nativeCarouselIndexRef.current = targetIndex;
          });
        }
      };

      if (direction === 'left') {
        if (i >= oldLen - 1) {
          apply();
        } else {
          ref.current.scrollTo?.({ index: i + 1, animated: true, onFinished: apply });
        }
      } else if (i <= 0) {
        apply();
      } else {
        ref.current.scrollTo?.({ index: i - 1, animated: true, onFinished: apply });
      }
    },
    [filteredContacts, applyScope],
  );

  const onClassifyPanEnd = useCallback(
    (translationX: number, velocityX: number) => {
      const minDistance = 72;
      const minSpeed = 520;
      if (translationX < -minDistance || velocityX < -minSpeed) {
        handleNativeClassifySwipe('left');
      } else if (translationX > minDistance || velocityX > minSpeed) {
        handleNativeClassifySwipe('right');
      }
    },
    [handleNativeClassifySwipe],
  );

  const pan =
    Platform.OS !== 'web' && Gesture && runOnJS
      ? Gesture.Pan()
          .activeOffsetX([-24, 24])
          .onEnd((e: { translationX: number; velocityX?: number }) => {
            'worklet';
            runOnJS(onClassifyPanEnd)(e.translationX, e.velocityX ?? 0);
          })
      : null;

  const handleProfilePress = useCallback((contact: Contact) => {
    (navigation as any).navigate('Profile', {
      screen: 'DetailsGhostProfile',
      params: { profileId: contact.accountId },
    })
  }, [navigation])

  const remainingCount = filteredContacts.length
  const isPreparing = isLoading || classifiedAtOpen === null
  // Everything is sorted: the pile can be started again on purpose.
  const canClassifyAgain =
    remainingCount === 0 && ((classifiedAtOpen?.size ?? 0) > 0 || removedContactIds.length > 0)

  const classifyAgain = () => {
    if (userID) clearClassifiedIds(userID)
    setClassifiedAtOpen(new Set())
    setRemovedContactIds([])
  }

  return (
    <Screen>
      <Header
        title={t('contactsScreen.classifyProfiles')}
        iconLeft={isWeb? undefined : "arrowBack"}
        iconColor={theme.colors.secondary}
      />

      <FeatureTooltip
        id="classify_swipe"
        title={t('featureTooltip.classify_swipe.title')}
        description={t('featureTooltip.classify_swipe.description')}
        icon="classify"
      />

      {isPreparing && (
        <View style={{flex: 1, justifyContent: 'center', alignItems: 'center', gap: theme.spacing.md}}>
          <ActivityIndicator size="large" color={theme.colors.primary} />
        </View>
      )}

      {isError && (
        <View style={{flex: 1, justifyContent: 'center', alignItems: 'center', padding: theme.spacing.lg}}>
          <Text type='body' color={theme.colors.error}>{t('errors.title')}</Text>
        </View>
      )}

      {!isPreparing && !isError && (
        <Body {...bodyProps}>
          <View style={commonContactsStyles.row}>
            <View style={commonContactsStyles.roundedIcon}>
              <Icon name='refresh2' color={theme.colors.primary} onPress={() => isWeb ? rollback() : ref.current?.prev()}/>
            </View>
            <View style={commonContactsStyles.flexShadowText}>
              <Text type='bodyBold' color={theme.colors.primary}>
                {remainingCount > 0
                  ? `${remainingCount} ${remainingCount > 1 ? t('contactsScreen.profilesRemainingToClassify') : t('contactsScreen.profileRemainingToClassify')}`
                  : t('contactsScreen.noProfileToClassify')}
              </Text>
            </View>
            <View style={commonContactsStyles.roundedIcon}>
              <Icon name='next' color={theme.colors.primary} onPress={() => isWeb ? swipe('left') : ref.current?.next()}/>
            </View>
          </View>

          {canClassifyAgain && (
            <TouchableOpacity
              testID="classify-again"
              onPress={classifyAgain}
              style={{ alignSelf: 'center', padding: theme.spacing.md }}
            >
              <Text type='bodyBold' color={theme.colors.primary}>
                {t('contactsScreen.classifyAgain')}
              </Text>
            </TouchableOpacity>
          )}

          {isWeb ? (
            <View style={[commonContactsStyles.container, {minHeight: CARD_MIN_HEIGHT + (isDesktop ? 20 : 100)}]}>
              {filteredContacts.length > 0 && <View style={styles.containerCard}>
                {isDesktop && remainingCount > 0 && <ButtonSwipe scope={Scope.PRIVATE} text={t('common.private')} onPress={() => swipe('left')}/>}
                <View style={[styles.profileCardContainer, {maxWidth: Math.min(500, width - 60), minHeight: CARD_MIN_HEIGHT, ...(isDesktop ? {} : { height: 'calc(100% - 64px)' as any })}]}>
                  {filteredContacts.map((contact, index) => {
                    // Only the top of a long pile is drawn; indexes and refs stay
                    // those of the whole list.
                    if (index < currentIndex - 15) return null
                    const baseTranslateX = index % 3 === 0 ? 5 : (index % 3 === 1 ? 0 : -5);

                    return (
                      <TinderCard
                        // @ts-ignore
                        ref={childRefs[index]}
                        key={contact.id}
                        className='swipe-card'
                        onSwipe={(dir) => handleSwipe(dir, contact, index)}
                        onCardLeftScreen={() => outOfFrame(contact.id, index)}
                        preventSwipe={['up', 'down']}
                        // Classify on how far the card is dragged, not how fast:
                        // a calm slide past ~110px sorts it, instead of needing a
                        // quick flick (the library's velocity default).
                        swipeRequirementType="position"
                        swipeThreshold={110}
                      >
                        <View style={{
                          transform: `translateX(${baseTranslateX}px)`,
                          transformOrigin: 'center center',
                          width: '100%',
                          height: '100%',
                          userSelect: 'none',
                        } as any}>
                          <ProfileCard contact={contact} onPress={() => handleProfilePress(contact)} />
                        </View>
                      </TinderCard>
                    );
                  })}
                </View>
                {!isDesktop && <View style={commonContactsStyles.buttonsContainer}>
                  <ButtonSwipe scope={Scope.PRIVATE} text={t('common.private')} onPress={() => swipe('left')}/>
                  <ButtonSwipe scope={Scope.PROFESSIONAL} text={t('common.pro')} isRight onPress={() => swipe('right')}/>
                </View>}
                {isDesktop && remainingCount > 0 && <ButtonSwipe scope={Scope.PROFESSIONAL} text={t('common.pro')} isRight onPress={() => swipe('right')}/>}
              </View>}
            </View>
          ) : (
            <GestureDetector gesture={pan}>
              <View style={commonContactsStyles.container}>
                <View style={styles.containerCard}>
                  <Carousel
                    ref={ref}
                    data={filteredContacts}
                    enabled={false}
                    loop={false}
                    autoFillData={false}
                    defaultIndex={0}
                    windowSize={5}
                    width={windowWidth}
                    height={carouselHeight}
                    onSnapToItem={(idx: number) => {
                      nativeCarouselIndexRef.current = idx;
                    }}
                    renderItem={({ item }: { item: Contact; index: number }) => (
                      <View
                        style={[
                          styles.card,
                          {
                            marginHorizontal: theme.spacing.md,
                            flex: 1,
                            justifyContent: 'center',
                            minHeight: carouselHeight - theme.spacing.lg * 2,
                          },
                        ]}
                      >
                        <ProfileCard contact={item} onPress={() => handleProfilePress(item)} />
                      </View>
                    )}
                  />
                  <View style={commonContactsStyles.buttonsContainer}>
                    <ButtonSwipe
                      scope={Scope.PRIVATE}
                      text={t('common.private')}
                      onPress={() => handleNativeClassifySwipe('left')}
                    />
                    <ButtonSwipe
                      scope={Scope.PROFESSIONAL}
                      text={t('common.pro')}
                      isRight
                      onPress={() => handleNativeClassifySwipe('right')}
                    />
                  </View>
                </View>
              </View>
            </GestureDetector>
          )}
          <View style={[commonStyles.paddingHorizontal, {marginBottom: theme.spacing.xxl}]}>
            <Text type='bodySmall' center color={theme.colors.secondary}>
              {t('contactsScreen.swipeToClassify')}
            </Text>
          </View>
        </Body>
      )}
    </Screen>
  )
}
